'use client';

/**
 * **지금 워크스페이스에 걸려 있는 것** — 시작하기 · 체크리스트 카드가 함께 읽는다.
 *
 * 🔑 구독본이다(`useTrackedSourcesLoad`) — 치환 op 에 넘길 «지금 목록»이 여기서 나오고, 다른 탭에서
 *   고친 것도 다시 열지 않고 온다. 구독이 오기 전에는 auth 컨텍스트의 사본을 쓴다.
 * ⚠️ `workspaceId` 가 비면 구독하지 않는다(부르는 쪽이 끈다 — 체크리스트 카드가 대상이 아닐 때).
 */

import type { OnboardingSetRef, OnboardingCurrent } from 'src/sections/onboarding/utils/onboarding-state';

import { useMemo } from 'react';

import { usePlanAccess } from 'src/hooks/use-plan-access';

import { countTargetKeywordUsage } from 'src/utils/plan/track-capacity';

import { toAccountList } from 'src/sections/common/hooks/handler/use-track-register';
import { useTrackedSourcesLoad } from 'src/sections/common/hooks/load/use-tracked-sources-load';
import { isSlackConnected, COMPETITOR_SET_NAME } from 'src/sections/onboarding/utils/onboarding-state';

/**
 * @param workspaceId 지금 워크스페이스 — 비면 구독하지 않는다
 * @returns 살아 있는 문서에서 읽은 브랜드 키워드·자사 계정·알림·Slack·세트, 그리고 시작하기가 건 세트
 */
export function useOnboardingSources(workspaceId: string | null) {
  const { liveWorkspace, keywordCampaigns } = useTrackedSourcesLoad(workspaceId, { seeding: false });
  const { workspace: authWorkspace } = usePlanAccess();

  const doc = (liveWorkspace ?? (workspaceId ? authWorkspace : null) ?? null) as Record<string, any> | null;
  const brandKeywords = useMemo<string[]>(
    () => (Array.isArray(doc?.brandSetting?.brandKeyword) ? doc.brandSetting.brandKeyword.map(String) : []),
    [doc?.brandSetting?.brandKeyword],
  );
  const brandAccounts = useMemo<Record<string, any>[]>(
    () => (Array.isArray(doc?.brandSetting?.brandAccounts) ? doc.brandSetting.brandAccounts : []),
    [doc?.brandSetting?.brandAccounts],
  );
  const campaigns = useMemo<OnboardingSetRef[]>(
    () => keywordCampaigns
      .map((row) => ({
        id: String(row?.id ?? ''),
        name: String(row?.name ?? '').trim(),
        keywords: Array.isArray(row?.keywords) ? row.keywords.map(String) : [],
        accounts: toAccountList(Array.isArray(row?.accounts) ? row.accounts : []),
      }))
      .filter((row) => row.id),
    [keywordCampaigns],
  );
  /* 🔑 시작하기가 건 세트 — 끝낼 때 적은 `onboarding.setId`, 없으면(그 전에 끝낸 워크스페이스) 「경쟁사」 이름 */
  const setId = String(doc?.onboarding?.setId ?? '').trim();
  const onboardingSet = useMemo(
    () => campaigns.find((row) => (setId ? row.id === setId : row.name === COMPETITOR_SET_NAME)) ?? null,
    [campaigns, setId],
  );

  const current: OnboardingCurrent = useMemo(
    () => ({ brandKeywords, brandAccounts, sets: campaigns, earlierSet: setId ? onboardingSet : null }),
    [brandKeywords, brandAccounts, campaigns, setId, onboardingSet],
  );

  return {
    /** 살아 있는 문서가 왔나 — 오기 전에는 사본이다 */
    live: Boolean(liveWorkspace),
    status: doc?.onboarding?.status as unknown,
    /** 시작하기가 건 세트 id(`onboarding.setId`) — 없으면 빈 글자 */
    setId,
    brandKeywords,
    brandAccounts,
    brandMention: (doc?.messageSettings?.brandMention ?? null) as Record<string, unknown> | null,
    slackConnected: isSlackConnected(doc),
    campaigns,
    onboardingSet,
    current,
    /** 타겟 키워드 칸의 쓴 수 — 추적 관리·등록 창과 같은 셈(`track-capacity`) */
    targetKeywordUsed: countTargetKeywordUsage(keywordCampaigns),
  };
}
